"use client";

import { MicrophoneIcon } from "@heroicons/react/24/outline";
import { useDebouncedCallback } from "@shared/hooks";
import { Button, cn, Input } from "@ubud-ui/react-ui-kit";
import type {
  VoiceSearchConnectorParams,
  VoiceSearchWidgetDescription,
} from "instantsearch.js/es/connectors/voice-search/connectVoiceSearch";
import connectVoiceSearch from "instantsearch.js/es/connectors/voice-search/connectVoiceSearch";
import { useEffect, useRef, useState } from "react";
import { useConnector, useSearchBox } from "react-instantsearch";

export type UseVoiceSearchProps = VoiceSearchConnectorParams;
export function useVoiceSearch(props?: UseVoiceSearchProps) {
  return useConnector<VoiceSearchConnectorParams, VoiceSearchWidgetDescription>(connectVoiceSearch, props);
}

export function VoiceSearch({ props, onFinished }: { props?: UseVoiceSearchProps; onFinished?: (transcript: string) => void }) {
  const { isBrowserSupported, isListening, toggleListening, voiceListeningState } = useVoiceSearch({
    ...(props || {}),
    language: "id",
  });

  useEffect(() => {
    if (voiceListeningState?.status === "finished") {
      const transcript = voiceListeningState?.transcript;

      if (transcript && onFinished) {
        onFinished(transcript);
      }
    }
  }, [voiceListeningState?.status, voiceListeningState?.transcript, onFinished]);

  if (!isBrowserSupported) {
    return <></>;
  }

  return (
    <Button
      variant="ghost"
      size="icon"
      className="z-20 h-[calc(100%_-_8px)] w-8 absolute top-[50%] right-2 transform translate-y-[-50%] bg-white rounded-full"
      onClick={toggleListening}
    >
      {isListening && <div className="cursor-pointer absolute z-1 opacity-40 rounded-full h-full w-full bg-primary-200 animate-ping"></div>}
      <MicrophoneIcon className={cn("size-6", isListening ? "text-primary" : "")} />
    </Button>
  );
}

export function HeaderSearchBox({
  keyword,
  initialKeyword,
  onSearch,
  onSearching,
}: {
  keyword: string;
  initialKeyword?: string | null;
  onSearch?: (keyword: string) => void;
  onSearching?: (isSearching?: boolean) => void;
}) {
  const { refine } = useSearchBox();
  const inputRef = useRef<HTMLInputElement>(null);

  const [isSearching, setIsSearching] = useState<boolean>(false);

  useEffect(() => {
    if (initialKeyword) {
      refine(initialKeyword);
    }
  }, [initialKeyword, refine]);

  const handleSearch = (val: string) => {
    setIsSearching(false);
    if (onSearching) {
      onSearching(false);
    }

    if (onSearch) {
      onSearch(val);
    }
    if (val) {
      refine(val);
    }
  };
  const debouncedRefine = useDebouncedCallback(handleSearch, 3000);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value?.length >= 5) {
      setIsSearching(true);
      if (onSearching) {
        onSearching(true);
      }

      debouncedRefine(e.target.value);
    }
  };

  const handleVoiceSearch = (val: string) => {
    if (inputRef && inputRef.current) {
      inputRef.current.value = val;
      if (onSearch) {
        onSearch(val);
      }
    }
  };

  return (
    <div className="sticky top-1 bg-white mb-2">
      <Input
        ref={inputRef}
        defaultValue={keyword}
        onChange={handleChange}
        placeholder="Cari pekerjaan yang kamu inginkan (min: 5 karakter)"
        className="m-0 text-sm"
      />
      {isSearching && (
        <Button
          variant="ghost"
          size="icon"
          className="z-20 h-[calc(100%_-_8px)] w-8 absolute top-[50%] right-10 transform translate-y-[-50%] bg-white rounded-full"
        >
          <span className="w-5 h-5 rounded-full border-t-2 border-l-2 border-r-2  border-primary inline-block animate-spin"></span>
        </Button>
      )}
      <VoiceSearch onFinished={handleVoiceSearch} />
    </div>
  );
}
